import React, { FC, useCallback } from 'react';
import { useLocale } from '../LocaleProvider';
import { useProcessPlaceholders } from '../utils/processPlaceholders';
import { UtilsContext } from './UtilsContext';
import type { Utils, UtilsProviderProps } from './UtilsProvider.types';

const getFallbackTranslation = (key: string) => `!${key}!`;

export const UtilsProvider: FC<UtilsProviderProps> = ({ utils, children }) => {
  const locale = useLocale();

  const { translate, ...otherUtils } = utils;
  const {
    media: { getMediaUrl },
    getLiveChatSectionColors,
  } = otherUtils;

  const translateWithVariousCases: Utils['translate'] = useCallback(
    (key, values) => {
      if (!key) {
        return '';
      }

      const keyVariants = [key, key.toUpperCase(), key.toLowerCase()];
      const fallbackTranslation = getFallbackTranslation(key);

      const [correctTranslation] = keyVariants
        .map((keyVariant) => [translate(keyVariant, values), getFallbackTranslation(keyVariant)])
        .find(([translation, fallback]) => translation !== fallback) || [fallbackTranslation];

      return correctTranslation;
    },
    [translate],
  );

  const processPlaceholders = useProcessPlaceholders({ locale, translate, getMediaUrl, getLiveChatSectionColors });

  return (
    <UtilsContext.Provider value={{ ...otherUtils, processPlaceholders, translate: translateWithVariousCases }}>
      {children}
    </UtilsContext.Provider>
  );
};
